/* The panels every console is built out of.
 *
 * NAMES. Not counts, and there is no longer a switch: a panel that says "6 members
 * at risk" has hidden the only thing anybody can act on.
 *
 * The counts treatment was built alongside this one so the two could be compared
 * side by side rather than argued about, with a switch in the top bar. The
 * comparison was made and names won, so the switch and the other treatment are
 * gone. A toggle left behind after a ruling is a toggle that invites the argument
 * again, on a demo, in front of somebody.
 */
import type { ReactNode } from 'react';
import type { Member } from '@acrostic/model';
import { NoneRow } from '../../components/Panel.js';
import { Avatar } from '../../components/Avatar.js';
import { tone } from '../../components/People.js';
import { go } from '../../routes.js';

export interface PeoplePanelProps {
  eyebrow: string;
  title: string;
  people: Member[];
  /** The sentence under the name. What is true of THIS person, not of the panel. */
  line: (m: Member) => string;
  /** Shown on the right of the heading. */
  right?: ReactNode;
  empty: string;
  /** Whether to print the risk dot and score. Off for lists that are not about risk. */
  scored?: boolean;
  /** An extra control per row. */
  actions?: (m: Member) => ReactNode;
  /** A line under the whole panel. */
}

export function PeoplePanel(p: PeoplePanelProps) {
  return (
    <section className="panel">
      <header>
        <div className="htitle">
          <h2 className="t-heading-h3">{p.title}</h2>
          <span className="eyebrow t-caption">{p.eyebrow}</span>
        </div>
        {p.right}
      </header>
      {!p.people.length ? <NoneRow>{p.empty}</NoneRow> : (
        <div className="rows">
          {p.people.map((m) => (
            <Row key={m.id} m={m} line={p.line} scored={p.scored ?? true} actions={p.actions} />
          ))}
        </div>
      )}
    </section>
  );
}

function Row({ m, line, scored, actions }: {
  m: Member; line: (m: Member) => string; scored: boolean;
  actions?: ((m: Member) => ReactNode) | undefined;
}) {
  /* SITE-RULES 1: a suppressed member holds NO score. Not a low one, not a stale one. */
  const score = m.suppression ? null : m.risk?.score ?? null;
  /* ONE BADGE, NOT A DOT AND A NUMBER. The design file's List item carries a single
     Status Badge on the right and nothing on the left but the face. That is also the
     better shape for a reason beyond matching it: the dot and the number were two
     renderings of one value at opposite ends of the row, so a tone disagreeing with
     its own number was expressible. Now the tone IS the badge the number sits in.
     `scored` still governs the whole thing, because the floor seats get the order and
     the reason with no number and no tier of any kind (PRD 4). */
  return (
    <div className={'row' + (actions ? ' stacked' : '')}>
      <Avatar name={m.name} photoUrl={m.photoUrl} size="sm" />
      <div className="who">
        <button type="button" className="n t-label-large namelink" onClick={() => go('/members/' + m.id)}>
          {m.name}
        </button>
        <div className="s t-body-small muted">{line(m)}</div>
        {actions && <div className="rowacts">{actions(m)}</div>}
      </div>
      {scored && (score == null
        ? <span className="pill flat t-label-small">Not scored</span>
        : <span className={'pill score t-label-small ' + tone(score)}
            title={`Member score ${score} of 100. It counts down: low needs attention.`}>{score}</span>)}
    </div>
  );
}

/* WHO THE MODEL DECLINES TO SCORE, AND WHY. The same words on every console, so a
   manager and a support coach reading the same gym read the same sentence. */
export function riskEyebrow(suppressed: number, onboarding: number): string {
  const bits: string[] = [];
  if (onboarding) bits.push(`${onboarding} in their first 90 days`);
  if (suppressed) bits.push(`${suppressed} on Care Track or Hold`);
  return bits.length ? `Not scored: ${bits.join(', ')}` : 'Everybody here is on the model';
}

/** "1 member" / "6 members", written once. */
export function plural(n: number, one: string, many = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`;
}

